'use client'

import { useEffect, useMemo, useRef } from 'react'
import { normalizeDigits } from '@hamishon/shared-utils'

interface OtpInputsProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  autoFocus?: boolean
}

const INPUT_COUNT = 6

export default function OtpInputs({ value, onChange, disabled, autoFocus }: OtpInputsProps) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([])
  const digits = useMemo(() => {
    const normalized = value.replace(/\s/g, '')
    return Array.from({ length: INPUT_COUNT }, (_, index) => normalized[index] || '')
  }, [value])

  useEffect(() => {
    if (autoFocus) {
      inputsRef.current[0]?.focus()
    }
  }, [autoFocus])

  const updateValue = (next: string) => {
    const trimmed = normalizeDigits(next).replace(/\D/g, '').slice(0, INPUT_COUNT)
    onChange(trimmed)
  }

  const handleChange = (index: number, nextValue: string) => {
    const sanitized = normalizeDigits(nextValue).replace(/\D/g, '')
    if (!sanitized) {
      const next = [...digits]
      next[index] = ''
      updateValue(next.join(''))
      return
    }

    const next = [...digits]
    next[index] = sanitized[0]
    updateValue(next.join(''))
    if (index < INPUT_COUNT - 1) {
      inputsRef.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus()
    }
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    const pasted = event.clipboardData.getData('text')
    updateValue(pasted)
  }

  return (
    <div className="flex justify-center gap-2" dir="ltr">
      {Array.from({ length: INPUT_COUNT }).map((_, index) => (
        <input
          key={index}
          ref={(el) => {
            inputsRef.current[index] = el
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[index]}
          disabled={disabled}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
          className="w-12 h-12 rounded-lg border border-outline text-center text-title-md font-semibold bg-surface text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
        />
      ))}
    </div>
  )
}
